<template>
  <div>
    <dd-form-page
        submit-route="web_api.v1.task.store"
        class="create-or-edit-task-page"
        v-bind:class="{ loaded }"
        :header="pageHeader"
        @submit="onSubmit"
        :valid-on-init="false"
        :save-per-step="isEditing"
        :success-message="taskSuccessMessage"
        :loading="loading"
        data-dusk="create-or-edit-task-page"
    >
      <template #task-buttons>
        <dd-button
            v-if="isEditing && canDeleteTask"
            @click="() => {deleteModalVisible = true}"
            type="danger"
        >
          <template #icon>
            <delete-icon class="icon-size-md"></delete-icon>
          </template>
          Taak verwijderen
        </dd-button>
      </template>

      <template #steps>
        <dd-step>
          <div class="flex-col gap-md">
            <dd-card header="Basigegevens">
              <dd-form-grid>
                <dd-form-grid-item>
                  <dd-select name="template"
                             @select="onTemplateSelect"
                             v-model="template"
                             title="Template"
                             :options="templateOptions"
                             data-dusk="task-template"
                             :searchable="true"
                  >
                  </dd-select>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-select name="type"
                             rules="required"
                             v-model="type"
                             title="Type"
                             data-dusk="task-type">
                    <a-select-option v-for="(type, key) in typeOptions" :key="key" :value="key">
                      {{ type }}
                    </a-select-option>
                  </dd-select>
                </dd-form-grid-item>
              </dd-form-grid>
            </dd-card>

            <dd-card header="Beschrijf taak">
              <dd-form-grid>
                <dd-form-grid-item>
                  <dd-input name="content"
                            v-model="content"
                            title="Beknopte omschrijving"
                            data-dusk="task-content"
                  ></dd-input>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-textarea name="description"
                               v-model="description"
                               title="Notitie toevoegen"
                               data-dusk="task-description"
                  ></dd-textarea>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-date-picker name="date"
                                  v-model="date"
                                  :value-format="dateFormat"
                                  title="Datum"
                                  data-dusk="task-date"
                  ></dd-date-picker>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <div class="flex-row gap-sm">
                    <a-radio-group v-model="date" button-style="solid">
                      <a-radio-button :value="today">Vandaag</a-radio-button>
                      <a-radio-button :value="tomorrow">Morgen</a-radio-button>
                      <a-radio-button :value="oneWeek">1 week</a-radio-button>
                      <a-radio-button :value="secondWeek">2 weken</a-radio-button>
                      <a-radio-button :value="threeWeeks">3 weken</a-radio-button>
                      <a-radio-button :value="oneMonth">1 maand</a-radio-button>
                      <a-radio-button :value="oneYear">1 jaar</a-radio-button>
                      <a-radio-button :value="threeYears">3 jaar</a-radio-button>
                      <a-radio-button :value="fiveYears">5 jaar</a-radio-button>
                    </a-radio-group>
                  </div>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-select name="priority"
                             v-model="priority"
                             title="Prioriteit"
                             data-dusk="task-priority"
                  >
                    <a-select-option value="0">
                      <dd-pill :small="false" color="#ff0000">Kritiek</dd-pill>
                    </a-select-option>
                    <a-select-option value="1" class="important">
                      <dd-pill :small="false" color="#ee7600">Belangrijk</dd-pill>
                    </a-select-option>
                    <a-select-option value="2" class="normal">
                      <dd-pill :small="false" type="primary">Normaal</dd-pill>
                    </a-select-option>
                    <a-select-option value="3" class="insignificant">
                      <dd-pill :small="false" color="#757575">Onbelangrijk</dd-pill>
                    </a-select-option>
                  </dd-select>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-radio-group name="status"
                                  v-model="status"
                                  title="Status"
                                  data-dusk="task-status"
                  >
                    <dd-radio :value="0">
                      Actief
                    </dd-radio>
                    <dd-radio :value="1">
                      Afgerond
                    </dd-radio>
                  </dd-radio-group>
                </dd-form-grid-item>
              </dd-form-grid>
            </dd-card>

            <dd-card header="Toewijzen aan">
              <dd-form-grid>
                <dd-form-grid-item>
                  <dd-select
                      name="category_id"
                      v-model="category_id"
                      title="Categorie"
                      :options="categoryOptions"
                      @select="handleCategorySelected"
                      data-dusk="task-category"
                  ></dd-select>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-select name="assigned_users"
                             mode="multiple"
                             v-model="assigned_users"
                             title="Toewijzen aan gebruiker(s)"
                             :options="userOptions"
                             data-dusk="task-users"
                  ></dd-select>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-select name="assigned_departments"
                             mode="multiple"
                             v-model="assigned_departments"
                             title="Toewijzen aan afdeling(en)"
                             data-dusk="task-departments"
                  >
                    <a-select-option v-for="(department, key) in departmentOptions" :key="key" :value="key">
                      {{ department }}
                    </a-select-option>
                  </dd-select>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-select name="productLabels"
                             mode="multiple"
                             v-model="productLabels"
                             title="Labels"
                             data-dusk="task-product-labels"
                  >
                    <a-select-option v-for="(productLabel, key) in productLabelOptions" :key="key" :value="key">
                      {{ productLabel }}
                    </a-select-option>
                  </dd-select>
                </dd-form-grid-item>
                <dd-form-grid-item>
                  <dd-customer-select name="customer_id"
                                      title="Klant kiezen"
                                      v-model="customer_id"
                                      data-dusk="task-customer"
                  ></dd-customer-select>
                </dd-form-grid-item>
                <dd-form-grid-item :cols="1">
                  <dd-switch name="visible_for_customer"
                             title="Zichtbaar voor klant"
                             v-model="visible_for_customer"
                             data-dusk="task-visible-for-customer"
                  ></dd-switch>
                </dd-form-grid-item>
                <dd-form-grid-item v-if="!isEditing" :cols="1">
                  <dd-switch name="create_note"
                             title="Ook als notitie aanmaken"
                             v-model="create_note"
                             data-dusk="task-create-note"
                  ></dd-switch>
                </dd-form-grid-item>
              </dd-form-grid>
            </dd-card>
          </div>
        </dd-step>
      </template>
    </dd-form-page>

    <delete-modal
        :visible="isEditing && deleteModalVisible"
        :task="{
          id: taskId,
          content: content,
        }"
        :description="`Weet je zeker dat je deze taak '${content || ''}' wil verwijderen?`"
        @cancel="() => {deleteModalVisible = false}"
        @deleted="afterDelete"
    ></delete-modal>
  </div>
</template>

<script>
import store from "./store";
import {mapFields} from 'vuex-map-fields';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import moment from 'moment';
import {DateFormatEnum} from "../../../enums";
import {Skeleton} from 'vue-loading-skeleton';
import DeleteModal from "~/modules/task/dialog-modal/DeleteModal.vue";

export default {
  name: "CreateOrEditTaskPage",
  components: {
    DeleteModal,
    Skeleton,
  },
  beforeDestroy() {
    this.resetTaskForm();
  },
  data() {
    return {
      loaded: false,
      deleteModalVisible: false,
    }
  },
  beforeCreate() {
    const STORE_KEY = '$_task_create_page'
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store)
    }
  },
  created() {
    if (this.taskId) {
      this.setTaskId(this.taskId);
    }
    if (this.$route?.params?.category) {
      this.setCategory(this.$route?.params?.category);
      this.setTaskCategoryId(this.$route?.params?.category);
    }

    if (this.isEditing) {
      this.initForEdit();
    } else {
      this.initForCreate();
    }
    if (this.customerId) {
      this.setTaskCustomerId(this.customerId);
    }

    this.loaded = true
  },
  computed: {
    taskSuccessMessage() {
      return this.isEditing ? 'Taak is opgeslagen!' : 'Taak is aangemaakt!';
    },
    instanceId() {
      return this.instance?.id;
    },
    instanceClass() {
      return this.instance?.class;
    },
    instanceType() {
      return this.instance?.type;
    },
    instanceName() {
      return this.instance?.name;
    },
    customerId() {
      return this.$route?.params?.customer;
    },
    taskId() {
      return this.$route.params?.task;
    },
    categoryId() {
      return this.$route.params?.category;
    },
    redirect() {
      return this.$route.query?.redirect;
    },
    categoryName() {
      return this.categories[this.task?.category_id];
    },
    pageHeader() {
      let header = 'Taak';
      if (this.instanceName) {
        return `${header} ${this.pageActionType} voor ${this.instanceName}`
      } else if (this.category) {
        return `${header} ${this.pageActionType} voor ${this.categoryName}`;
      } else {
        return `${header} ${this.pageActionType}`;
      }
    },
    today() {
      return moment().format(DateFormatEnum.DATE);
    },
    tomorrow() {
      return moment().add(1, 'day').format(DateFormatEnum.DATE);
    },
    oneWeek() {
      return moment().add(1, 'week').format(DateFormatEnum.DATE);
    },
    secondWeek() {
      return moment().add(2, 'weeks').format(DateFormatEnum.DATE);
    },
    threeWeeks() {
      return moment().add(3, 'weeks').format(DateFormatEnum.DATE);
    },
    oneMonth() {
      return moment().add(1, 'month').format(DateFormatEnum.DATE);
    },
    oneYear() {
      return moment().add(1, 'year').format(DateFormatEnum.DATE);
    },
    threeYears() {
      return moment().add(3, 'years').format(DateFormatEnum.DATE);
    },
    fiveYears() {
      return moment().add(5, 'years').format(DateFormatEnum.DATE);
    },
    pageActionType() {
      return this.isEditing ? 'bewerken' : 'aanmaken';
    },
    dateFormat() {
      return DateFormatEnum.DATE;
    },
    ...mapGetters(`$_task_create_page`, {
      isEditing: 'isEditing',
      activeCategory: 'activeCategory',
      userOptions: 'userOptions',
      activeTemplate: 'activeTemplate',
      templateOptions: 'templateOptions',
      categoryOptions: 'categoryOptions',
    }),
    ...mapFields(`$_task_create_page`, [
      'task.priority',
      'task.assigned_departments',
      'task.productLabels',
      'task.assigned_users',
      'task.category_id',
      'task.status',
      'task.type',
      'task.date',
      'task.customer_id',
      'task.content',
      'task.description',
      'task.visible_for_customer',
      'task.create_note',
      'task.template',
    ]),
    ...mapState(`$_task_create_page`, {
      axiosError: 'axiosError',
      errors: 'errors',
      task: 'task',
      departmentOptions: 'departments',
      productLabelOptions: 'productLabels',
      categories: 'categories',
      typeOptions: 'types',
      loading: 'loading',
    }),
    ...mapState({
      me: 'me',
      instance: 'instance',
    }),
    canDeleteTask() {
      return this.$store.state.permissions.delete_task === true;
    }
  },
  methods: {
    initForCreate() {
      if (this.$route?.params?.class && this.$route?.params?.id) {
        this.setInstance({
          class: this.$route?.params?.class,
          id: this.$route?.params?.id,
        });
        this.getInstance();
      }
      this.getMe().then(() => {
        this.setTask({
          ...this.task,
          user_id: this.me?.id,
        })
      });
      if (this.customerId) {
        this.setTaskCustomerId(this.customerId);
      }
      this.getInitialCreatePageData().then(() => {
        this.setLoading(false);
      })
    },
    initForEdit() {
      const initialPromise = this.getInitialEditPageData();
      const fillTaskPromise = this.fillTaskState();
      Promise.all([initialPromise, fillTaskPromise])
          .then(() => {
            this.setLoading(false);
          });
    },
    onTemplateSelect() {
      this.$confirm({
        content: 'Reeds ingevulde velden kunnen worden overschreven. Wil je doorgaan?',
        okText: 'Ja',
        cancelText: 'Annuleren',
        onOk: () => {
          // Set all values to template value, if it doesn't have any values yet.
          let template = this.activeTemplate;
          if (!template) return;

          let fillableTask = {...this.task};

          fillableTask.priority = template.priority;
          fillableTask.category_id = template.category_id;
          fillableTask.status = template.status;
          fillableTask.content = template.content;
          fillableTask.description = template.description;
          fillableTask.category_id = template.category_id;
          fillableTask.visible_for_customer = !!template.visible_for_customer;
          fillableTask.date = moment().add(template.date, 'days').format(this.dateFormat);
          fillableTask.type = template.type;

          fillableTask.assigned_users = template.users.data.map(item => item.id);
          fillableTask.assigned_departments = template.departments.data.map(item => item.id);
          fillableTask.productLabels = template.product_labels.data.map(item => item.id);

          this.setTask(fillableTask);
        },
        onCancel: () => {
          this.template = null;
        },
      });
    },
    ...mapMutations(`$_task_create_page`, {
      setTaskCustomerId: 'SET_TASK_CUSTOMER_ID',
      setTaskId: 'SET_TASK_ID',
      setTask: 'SET_TASK',
      setCategory: 'SET_CATEGORY',
      setTaskUserId: 'SET_TASK_USER_ID',
      setTaskCategoryId: 'SET_TASK_CATEGORY_ID',
      resetTaskForm: 'RESET_TASK_FORM',
      setLoading: 'SET_LOADING',
    }),
    ...mapMutations({
      setInstance: 'SET_PAGE_INSTANCE',
    }),
    ...mapActions({
      getMe: 'getMe',
      getInstance: 'getInstance',
    }),
    ...mapActions(`$_task_create_page`, {
      reloadTemplates: 'reloadTemplates',
    }),
    ...mapActions(`$_task_create_page`, {
      fillTaskState: 'fillTaskState',
      getInitialEditPageData: 'getInitialEditPageData',
      getInitialCreatePageData: 'getInitialCreatePageData',
      saveTask: 'saveTask',
    }),
    handleCategorySelected() {
      this.template = null;
      this.reloadTemplates();
    },
    onSubmit() {
      this.saveTask(this.redirect);
    },
    afterDelete() {
      this.deleteModalVisible = false;
      this.$emit("deleted", this.taskId);
      this.navigator.back();
    },
  },
  watch: {
    me() {
      this.setTaskUserId(this.me?.id);
    },
    instanceType() {
      this.setTask({
        ...this.task,
        instance_id: this.instanceId,
        instance_type: this.instanceType,
      })
    }
  },
};
</script>

<style lang="scss">
@import "~@/_default.scss";

.form-input.priority {
  li {
    color: white;
    font-size: 13px;
    font-weight: 500;
    border-radius: 9999px;

    .critical {
      background-color: $brand-danger;
    }

    .important {
      background-color: orange;
    }

    .normal {
      background-color: $brand-warning;
    }

    .normal {
      background-color: $brand-primary;
    }
  }
}
</style>
